import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";

import moment from "moment-timezone";

import Skeleton from "@mui/material/Skeleton";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";

import { CardSolidGame } from "./CardSolidGame";
import Modal from "../Components/Modal";

import { useAuth } from "../hooks/useAuth";
import useLocation from "../hooks/useLocation";
import usePageTitle from "../hooks/usePageTitle";

import RightArrow from "../assets/Right-arrow.png";
import LeftArrow from "../assets/Left-arrow.png";

function Restaurant() {
  const { id } = useParams();
  const [isNonAuthedModalOpen, toggleIsNonAuthedModalOpen] = useState(false);

  const { authed } = useAuth();
  const { location, locationEvents, error, loading } = useLocation(id);

  const inThreeHours = moment().add(3, "h");

  const activeEvents = locationEvents?.filter((event) => {
    return moment(event.date).isAfter(inThreeHours);
  });

  const navigate = useNavigate();

  const handleJoinEventClicked = (eventId) => {
    navigate(`/events/${eventId}`);
  };

  const handleCreateEventClicked = (locationId) => {
    if (authed) {
      navigate(`/restaurants/${locationId}/create`);
    } else {
      toggleIsNonAuthedModalOpen(true);
    }
  };

  useEffect(() => {
    if (error) {
      navigate("/restaurants");
    }
  }, [error, navigate]);

  usePageTitle(`${location?.title} | Fudopia | ${location?.city?.title}`);

  function NonAuthedModal() {
    return (
      <Modal
        title="Be one of the first foodies in Bangkok!"
        isOpen={isNonAuthedModalOpen}
        handleDismiss={() => toggleIsNonAuthedModalOpen(!isNonAuthedModalOpen)}
      >
        <p style={{ textAlign: "center" }}>
          We’re offering free event access for the first 200 signups. No
          subscription needed for a whole year.
        </p>
        <p style={{ marginBottom: 40, textAlign: "center" }}>
          Create or join foodie meetups and help us kick things off in the
          tastiest way.
        </p>
        <div className="btn-inline">
          <Button
            color="primary"
            variant="contained"
            onClick={() => toggleIsNonAuthedModalOpen(!isNonAuthedModalOpen)}
          >
            Not now
          </Button>
          <Button
            color="secondary"
            variant="contained"
            onClick={() => navigate("/login")}
          >
            Login/Register
          </Button>
        </div>
      </Modal>
    );
  }
  return (
    <>
      <NonAuthedModal />
      {error && <p className="error-message">Something went wrong...</p>}
      {loading && (
        <div className="block-wrapper">
          <Skeleton animation="wave" height={600}></Skeleton>
        </div>
      )}
      {!loading && location && (
        <>
          <Typography
            variant="h1"
            component="div"
            sx={{ flexGrow: 0, marginRight: 3 }}
          >
            {location.title}
          </Typography>
          <div className="block restaurant">
            <p className="p-wrapper centered">{location.body}</p>
            <div className="image-wrapper">
              <img
                src={location?.image_url}
                className="single-image"
                alt={location.title}
              />
            </div>
            <div className="section">
              {location.dishes.length > 0 && (
                <>
                  <h2 className="subheader" style={{ paddingBottom: "40px" }}>
                    Specialty Dishes
                  </h2>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-around",
                      alignItems: "center",
                      marginTop: 2,
                      maxWidth: "850px",
                      marginLeft: "auto",
                      marginRight: "auto",
                    }}
                  >
                    <CardSolidGame dishes={location.dishes} />
                  </Box>
                </>
              )}
            </div>

            <div className="section">
              <h2 className="subheader">Address</h2>
              {location.google_maps ? (
                <Link
                  to={location.google_maps}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span>{location.address}</span>
                </Link>
              ) : (
                <span>{location.address}</span>
              )}
            </div>

            {location.opening_hours && (
              <div className="section">
                <h2 className="subheader">Opening Hours</h2>
                <span>{location.opening_hours}</span>
              </div>
            )}

            <div className="arrow-container">
              <img
                className="right-arrow arrow"
                src={RightArrow}
                alt="right arrow illustration"
              />
              <img
                className="left-arrow arrow"
                src={LeftArrow}
                alt="left arrow illustration"
              />
              <div className="button-wrapper">
                <Button
                  color="primary"
                  variant="contained"
                  onClick={() => handleCreateEventClicked(location.id)}
                >
                  Create an Event
                </Button>
              </div>
            </div>

            <div className="section">
              <Link
                to="/restaurants"
                className="back-button"
                style={{ textDecoration: "underline" }}
              >
                ← Back to the Restaurants
              </Link>
            </div>
          </div>

          {!loading && activeEvents?.length > 0 && (
            <div className="section">
              <h2>See events in {location.title}</h2>
              <div className="block-wrapper events">
                {activeEvents?.map((event, key) => (
                  <div className="block" key={key}>
                    <h3
                      style={{ cursor: "pointer", fontSize: "1.5rem" }}
                      onClick={() => handleJoinEventClicked(event.id)}
                    >
                      {event.title}
                    </h3>
                    <h3 className="subheader">Date & Time</h3>
                    <span>
                      {moment(event.date)
                        .tz(event.timezone)
                        .format(
                          authed ? "dddd, MMMM Do · h:mm a" : "dddd, MMMM Do"
                        )}
                    </span>
                    {event.tags.length > 0 && (
                      <>
                        <h3 className="subheader">Meetup Mood</h3>
                        <ul className="tags-list">
                          {event.tags.map((tag) => (
                            <li key={tag.id}>{tag.title}</li>
                          ))}
                        </ul>
                      </>
                    )}
                    <h3 className="subheader">What are we trying?</h3>
                    <img
                      src={event.dish?.image_url}
                      alt={event.dish?.title}
                      className="event-dish"
                    />
                    {event.clients?.length === 4 ? (
                      <p>This event is full</p>
                    ) : (
                      <div className="sm-button-wrapper">
                        <Button
                          color="primary"
                          variant="contained"
                          size="small"
                          className="event-list-button"
                          onClick={() => handleJoinEventClicked(event.id)}
                        >
                          Join this event
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
}

export default Restaurant;
